import { useGameStore } from '@/store/gameStore'
import {
  repairCourt, getRepairCost, repairBuilding, getBuildingRepairCost,
} from '@/game/systems/MaintenanceSystem'
import { getFacilityDef } from '@/config/facilityRegistry'
import { FACILITY_VISUALS, getFacilityAsset } from '@/config/campusConfig'
import { SectionDeck, EmptyStateRich } from '../components/CourtVision'
import { Wrench } from 'lucide-react'

const courtVisual = FACILITY_VISUALS.find(v => v.id === 'court')
const facilityImg = (id: string, tier: number) => {
  const v = FACILITY_VISUALS.find(x => x.id === id)
  return v ? getFacilityAsset(v, tier)?.image : undefined
}

/** Small structure thumbnail; falls back to a gradient if the asset is missing */
function Thumb({ image, condition }: { image?: string; condition: number }) {
  const ring = condition >= 80 ? 'var(--success)' : condition >= 50 ? 'var(--warning)' : 'var(--danger)'
  return (
    <span
      style={{
        width: 54, height: 40, flexShrink: 0, borderRadius: 'var(--radius-sm)',
        border: `1px solid color-mix(in srgb, ${ring} 55%, transparent)`,
        backgroundImage: image ? `url(${image})` : undefined,
        backgroundColor: '#152033',
        backgroundSize: 'cover', backgroundPosition: 'center',
      }}
    />
  )
}

function ConditionRow({
  title, sub, image, condition, cost, canRepair, onRepair,
}: {
  title: string
  sub: string
  image?: string
  condition: number
  cost: number
  canRepair: boolean
  onRepair: () => void
}) {
  const fillTone = condition >= 80 ? 'success' : condition >= 50 ? 'warning' : 'danger'
  return (
    <div className="row-tile">
      <Thumb image={image} condition={condition} />
      <div className="row-tile-main">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
          <div className="row-tile-title">{title}</div>
          <span className="meta-text">{sub}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '5px' }}>
          <div className="progress-track">
            <div className={`progress-fill angled ${fillTone}`} style={{ width: `${condition}%` }} />
          </div>
          <span className="meta-text" style={{ fontWeight: 700, minWidth: '34px', textAlign: 'right' }}>
            {Math.round(condition)}%
          </span>
        </div>
      </div>
      <div className="row-tile-side">
        {condition < 100 ? (
          <button
            onClick={onRepair}
            disabled={!canRepair}
            title={canRepair ? `Reparar por ${cost.toLocaleString('es-ES')}€` : 'Fondos insuficientes'}
            className="icon-btn"
            style={{
              fontSize: '0.7rem', fontWeight: 700,
              ...(canRepair ? { background: 'var(--gold-fill)', color: 'var(--gold)' } : {}),
            }}
          >
            <Wrench size={12} /> {cost.toLocaleString('es-ES')}€
          </button>
        ) : (
          <span className="pill pill-success">Impecable</span>
        )}
      </div>
    </div>
  )
}

export function MaintenanceTabContent() {
  const { courts, buildings, money, hasStaff } = useGameStore()

  if (courts.length === 0 && buildings.length === 0) {
    return (
      <EmptyStateRich
        icon={Wrench}
        title="Nada que mantener todavía"
        sub="Cuando construyas pistas y edificios, aquí vigilarás su desgaste y los pondrás a punto"
      />
    )
  }

  const courtsNeeding = courts.filter(c => (c.condition ?? 100) < 100).length
  const buildingsNeeding = buildings.filter(b => (b.condition ?? 100) < 100).length

  return (
    <div>
      {courts.length > 0 && (
        <SectionDeck
          title="Estado de pistas"
          icon={Wrench}
          meta={courtsNeeding > 0 ? `${courtsNeeding} con desgaste` : 'Todo en orden'}
        >
          <div className="space-y-2">
            {courts.map((court, i) => {
              const condition = court.condition ?? 100
              const cost = getRepairCost(condition, court.type)
              const tier = court.type === 'indoor' ? 3 : 0
              return (
                <ConditionRow
                  key={court.id}
                  title={`Pista ${i + 1}`}
                  sub={court.type === 'indoor' ? 'Indoor' : 'Outdoor'}
                  image={courtVisual ? getFacilityAsset(courtVisual, tier)?.image : undefined}
                  condition={condition}
                  cost={cost}
                  canRepair={condition < 100 && money >= cost}
                  onRepair={() => repairCourt(court.id)}
                />
              )
            })}
          </div>
        </SectionDeck>
      )}

      {buildings.length > 0 && (
        <SectionDeck
          title="Estado de edificios"
          icon={Wrench}
          meta={buildingsNeeding > 0 ? `${buildingsNeeding} con desgaste` : 'Todo en orden'}
        >
          <div className="space-y-2">
            {buildings.map(building => {
              const condition = building.condition ?? 100
              const cost = getBuildingRepairCost(condition)
              const def = getFacilityDef(building.type)
              return (
                <ConditionRow
                  key={building.id}
                  title={def?.name ?? building.type}
                  sub={`Nv.${building.level}`}
                  image={facilityImg(building.type, building.level)}
                  condition={condition}
                  cost={cost}
                  canRepair={condition < 100 && money >= cost}
                  onRepair={() => repairBuilding(building.id)}
                />
              )
            })}
          </div>
        </SectionDeck>
      )}

      <div className="hint-box" style={{ marginTop: '0.75rem' }}>
        {hasStaff('maintenance')
          ? 'Tu técnico de mantenimiento repara pistas y edificios poco a poco cada semana.'
          : 'Contrata un técnico de mantenimiento: frena el deterioro y repara pistas y edificios cada semana.'}
      </div>
    </div>
  )
}
